import { useTranslation } from "react-i18next";
import type { IdeSession } from "~/api/opsTypes";
import { Select, TextArea } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";

export type DispatchMode = "chat" | "thought";

/**
 * Mode, target, text, send.
 *
 * The target list holds **online windows of the actor's own** and nothing else, because that
 * is precisely the set the server will accept (D1): a colleague's window answers
 * `403 not_your_session` on create, so offering one would be offering a refusal. The blank
 * first option means "any of mine" — the `targetSessionKey` is omitted, and any live window
 * of the actor's may claim it.
 */
export function Composer({
  mode,
  onModeChange,
  target,
  onTargetChange,
  value,
  onChange,
  onSend,
  sending,
  sessions,
}: {
  mode: DispatchMode;
  onModeChange: (mode: DispatchMode) => void;
  target: string;
  onTargetChange: (sessionKey: string) => void;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  sending: boolean;
  sessions: IdeSession[];
}) {
  const { t } = useTranslation();
  const online = sessions.filter((session) => session.isOwn && session.isOnline);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-2">
        <Select
          label={t("Mode")}
          value={mode}
          onChange={(event) => onModeChange(event.target.value as DispatchMode)}
        >
          <option value="chat">{t("Discussion")}</option>
          <option value="thought">{t("One-off task")}</option>
        </Select>
        <Select
          label={t("Target window")}
          value={target}
          onChange={(event) => onTargetChange(event.target.value)}
        >
          <option value="">{t("Any of my windows")}</option>
          {online.map((session) => (
            <option key={session.sessionKey} value={session.sessionKey}>
              {session.workspaceName ?? session.sessionKey}
            </option>
          ))}
        </Select>
      </div>

      {online.length === 0 ? (
        <p className="text-small text-text-secondary">
          {t("No IDE online — open VS Code with the Docit extension.")}
        </p>
      ) : null}

      <TextArea
        rows={3}
        value={value}
        aria-label={t("Message")}
        placeholder={
          mode === "chat"
            ? t("Ask your IDE… (⌘⏎ to send; follow-ups keep context)")
            : t("Describe the one-off task… (⌘⏎ to send)")
        }
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            onSend();
          }
        }}
      />

      <div>
        <Button disabled={sending || !value.trim()} onClick={onSend}>
          {sending ? t("Sending…") : mode === "chat" ? t("Send") : t("Dispatch task")}
        </Button>
      </div>
    </div>
  );
}
